import {
  ClipboardEvent,
  forwardRef,
  KeyboardEvent,
  TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { Bold, List, ListOrdered } from "lucide-react";
import { cn } from "@/utils/cn";
import { htmlToMarkdown } from "@/utils/htmlToMarkdown";
import { toggleBold, toggleList, type TextEdit } from "@/utils/markdownFormatting";

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  id: string;
  /**
   * Upper bound on auto-growth, in lines. Past this the field stops expanding
   * and scrolls internally, so one long value can't push the rest of a form
   * off screen. Omitted = grow without limit (the previous behaviour).
   */
  maxRows?: number;
  /**
   * Show a formatting bar (bold, bulleted list, numbered list; ⌘/Ctrl+B for
   * bold) above the field. The value stays a plain string: formatting is
   * written as the markdown FormattedText renders, so storage and every read
   * view are unchanged. Off by default — opt in for long-form answers that
   * are displayed with FormattedText.
   */
  richText?: boolean;
}

type FormatToolKey = "bold" | "bullets" | "numbers";

const FORMAT_TOOLS: { key: FormatToolKey; label: string; Icon: typeof Bold }[] = [
  { key: "bold", label: "Bold (⌘/Ctrl+B)", Icon: Bold },
  { key: "bullets", label: "Bulleted list", Icon: List },
  { key: "numbers", label: "Numbered list", Icon: ListOrdered },
];

function setNativeValue(element: HTMLTextAreaElement, value: string) {
  const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, "value")?.set;
  setter?.call(element, value);
}

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T) => {
    refs.forEach((r) => {
      if (!r) return;
      if (typeof r === "function") r(node);
      else (r as React.MutableRefObject<T | null>).current = node;
    });
  };
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { label, id, error, className = "", onPaste, onKeyDown, value, maxRows, richText, ...props },
    ref,
  ) => {
    const elementRef = useRef<HTMLTextAreaElement | null>(null);
    const showToolbar = richText && !props.disabled && !props.readOnly;

    // Writes a formatting result back through the same native-setter + input
    // event route as paste, so react-hook-form's register() and controlled
    // callers both see an ordinary change.
    const applyEdit = (edit: TextEdit) => {
      const el = elementRef.current;
      if (!el) return;
      setNativeValue(el, edit.value);
      el.dispatchEvent(new Event("input", { bubbles: true }));
      el.focus();
      el.setSelectionRange(edit.selectionStart, edit.selectionEnd);
    };

    const runTool = (tool: FormatToolKey) => {
      const el = elementRef.current;
      if (!el) return;
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      applyEdit(
        tool === "bold"
          ? toggleBold(el.value, start, end)
          : toggleList(el.value, start, end, tool === "bullets" ? "bullet" : "number"),
      );
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      onKeyDown?.(e);
      if (e.defaultPrevented || !showToolbar) return;
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "b") {
        e.preventDefault();
        runTool("bold");
      }
    };

    // Grows the textarea to fit its content instead of showing an internal
    // scrollbar -- up to `maxRows`, past which it scrolls internally rather
    // than growing without bound.
    const resize = useCallback(() => {
      const el = elementRef.current;
      if (!el) return;
      el.style.height = "auto";
      const content = el.scrollHeight;
      if (!maxRows) {
        el.style.height = `${content}px`;
        return;
      }
      // line-height can compute to "normal" on a font that doesn't set one;
      // fall back to a ratio of the font size so the cap never collapses to 0.
      const styles = window.getComputedStyle(el);
      const lineHeight =
        Number.parseFloat(styles.lineHeight) ||
        Number.parseFloat(styles.fontSize) * 1.5;
      const chrome =
        Number.parseFloat(styles.paddingTop) +
        Number.parseFloat(styles.paddingBottom) +
        Number.parseFloat(styles.borderTopWidth) +
        Number.parseFloat(styles.borderBottomWidth);
      const max = lineHeight * maxRows + chrome;
      el.style.height = `${Math.min(content, max)}px`;
      el.style.overflowY = content > max ? "auto" : "hidden";
    }, [maxRows]);

    useEffect(() => {
      resize();
    }, [resize, value]);

    useEffect(() => {
      const el = elementRef.current;
      if (!el) return;
      el.addEventListener("input", resize);
      return () => el.removeEventListener("input", resize);
    }, [resize]);

    // Uncontrolled use (react-hook-form's `register`) passes no `value`, so the
    // effect above never re-runs and the field opens at its `rows` height no
    // matter how long the content it was seeded with. Measuring the node's own
    // value on mount sizes those correctly too. useLayoutEffect so the height
    // is applied before paint -- with a plain effect the drawer visibly opens
    // short and then snaps taller.
    useLayoutEffect(() => {
      resize();
    }, [resize]);

    // A native <textarea> can only hold plain text, so pasted bold/heading/list
    // formatting is converted to visible markdown instead of being dropped.
    const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
      onPaste?.(e);
      if (e.defaultPrevented) return;

      const html = e.clipboardData.getData("text/html");
      if (!html) return;

      e.preventDefault();
      const converted = htmlToMarkdown(html);

      const target = e.currentTarget;
      const start = target.selectionStart ?? target.value.length;
      const end = target.selectionEnd ?? target.value.length;
      const newValue = target.value.slice(0, start) + converted + target.value.slice(end);
      const cursor = start + converted.length;

      setNativeValue(target, newValue);
      target.setSelectionRange(cursor, cursor);
      target.dispatchEvent(new Event("input", { bubbles: true }));
    };

    return (
      <>
        {label && (
          <label htmlFor={id} className="label">
            {label}
          </label>
        )}
        {showToolbar && (
          <div role="toolbar" aria-label="Text formatting" className="-mb-1 flex items-center gap-0.5">
            {FORMAT_TOOLS.map(({ key, label: toolLabel, Icon }) => (
              <button
                key={key}
                type="button"
                title={toolLabel}
                aria-label={toolLabel}
                aria-controls={id}
                // Keep focus (and so the selection) in the textarea.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => runTool(key)}
                className="flex h-7 w-7 items-center justify-center rounded-md text-theme-text-muted transition-colors hover:bg-theme-bg-hover hover:text-theme-text-heading"
              >
                <Icon size={14} strokeWidth={2.25} />
              </button>
            ))}
          </div>
        )}
        <textarea
          ref={mergeRefs(ref, elementRef)}
          id={id}
          aria-invalid={error ? true : undefined}
          className={cn(
            "textarea resize-none overflow-hidden",
            className,
            // After className, so it beats a caller's own border colour.
            error && "border-error-500 focus:border-error-500 focus:ring-error-500",
          )}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          value={value}
          {...props}
        />
        {error && (
          <p data-field-error className="error-message">
            {error}
          </p>
        )}
      </>
    );
  },
);

Textarea.displayName = "Textarea";
